/**
 * Human share surface — single pipeline for Share UI / Copy / PNG chronology.
 * Process tags (RFI/donk/…) never belong here; those stay in Pro (`notation`).
 * Domain modules: explainMode / reviewScope are UI intent; potShape honesty meta
 * is separate from this chronology renderer.
 */
import type { Street, StreetAction } from './types'

const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river']

/** Raw action verb for PNG (matches historical export: type as stored). */
export function rawActionVerb(type: StreetAction['type']): string {
  return type
}

/**
 * Soft E P4: display seat name only — stored actor `Hero` becomes the hero
 * seat (e.g. BTN). Label-only; stored actor stays `Hero` for matching.
 * Never appends process tags.
 */
export function displayActorSeat(
  actor: string,
  heroPosition?: string,
): string {
  const trimmed = actor.trim()
  if (heroPosition && trimmed.toLowerCase() === 'hero') return heroPosition
  return trimmed
}

/** Actor + verb + optional size — never appends process tags. */
export function formatHumanActionLine(
  action: StreetAction,
  verb: string,
  heroPosition?: string,
): string {
  const size = action.size ? ` ${action.size}` : ''
  const actor = displayActorSeat(action.actor, heroPosition)
  return `${actor}: ${verb}${size}`
}

export function groupActionsByStreet(
  actions: StreetAction[],
): { street: Street; items: StreetAction[] }[] {
  return STREET_ORDER.map((street) => ({
    street,
    items: actions.filter((a) => a.street === street),
  })).filter((g) => g.items.length > 0)
}

/**
 * Flat human chronology for Copy (localized street headers + bullets).
 * Callers supply labels so this module stays i18n-free.
 */
export function buildHumanActionChronologyLines(
  actions: StreetAction[],
  opts: {
    streetHeader: (street: Street) => string
    actionVerb: (type: StreetAction['type']) => string
    bullet?: string
    emptyLine: string
    /** Soft E P4: hero seat name for display (stored actor stays Hero). */
    heroPosition?: string
  },
): string[] {
  if (actions.length === 0) return [opts.emptyLine]
  const bullet = opts.bullet ?? '· '
  const lines: string[] = []
  let current: Street | null = null
  for (const a of actions) {
    if (a.street !== current) {
      if (current !== null) lines.push('')
      current = a.street
      lines.push(opts.streetHeader(a.street))
    }
    lines.push(
      `${bullet}${formatHumanActionLine(a, opts.actionVerb(a.type), opts.heroPosition)}`,
    )
  }
  return lines
}

/** PNG-style lines: `PF:` headers + indented `Actor: verb size` (no tags). */
export function buildPngActionChronologyLines(
  actions: StreetAction[],
  heroPosition?: string,
): string[] {
  const STREET: Record<Street, string> = {
    preflop: 'PF',
    flop: 'F',
    turn: 'T',
    river: 'R',
  }
  const lines: string[] = []
  let currentStreet: Street | null = null
  for (const a of actions) {
    if (a.street !== currentStreet) {
      currentStreet = a.street
      lines.push(`${STREET[a.street]}:`)
    }
    lines.push(`  ${formatHumanActionLine(a, rawActionVerb(a.type), heroPosition)}`)
  }
  return lines
}
